import { publicationStateForSave } from '../../utils/formPublication';
import { useOnboardingDriver } from '../onboarding/GuidedFormCreation/OnboardingDriverContext';
import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import AddToCartRender from './preview/AddToCartRender';
import { getAuthToken, getAuthGroups } from '../../utils/auth';
import { toast } from 'sonner';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import ElementEditorSection, {
  elementEditorSectionHasErrors,
  elementEditorSectionIcons,
  focusFirstInvalidElement,
} from '../workspaces/ElementEditorSection';
import '../../css/multi-select-styling.css';
import type {
  AddToCartFormData,
  CreateAddToCartDrawerProps,
  Tab,
  WorkspaceHeaderAction,
} from './types/AddToCartTypes';

// Import tab components
import AddToCartConfiguration from './tabs/AddToCartConfiguration';
import AppearanceTab from './tabs/AppearanceTab';

import type { FormData, ColorPickerColor } from './tabs/types';
import { portalClient } from '../../lib/trpc/portal';

// Utility function for classNames
function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ');
}

const tabs: Tab[] = [
  { name: 'Configuration', step: 'configuration', current: true },
  { name: 'Appearance', step: 'appearance', current: false },
];

const initialFormData: AddToCartFormData = {
  elementName: '',
  connectedForms: [],
  allowedDomains: [],
  disallowedDomains: [],
  customShareLink: '',
  color: '',
  sticky: null,
};

export default function CreateAddToCartDrawer(props: CreateAddToCartDrawerProps) {
  const paymentsReady = useOnboardingDriver()?.chargesEnabled === true;
  const useCreateWorkspaceCards = props.presentation === 'workspace';
  const [formData, setFormData] = useState<AddToCartFormData>(initialFormData);
  const [elementID, setElementID] = useState<string | null>(null);
  const [availableForms, setAvailableForms] = useState<FormData[]>([]);
  const [active, setActive] = useState<boolean>(true);

  // Add validation states
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState<string>('configuration');

  // Add loading states
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isActivating, setIsActivating] = useState<boolean>(false);

  // Color picker state
  const [sketchPickerColor, setSketchPickerColor] = useState<ColorPickerColor>({
    r: 0,
    g: 0,
    b: 0,
    a: 1,
  });
  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);

  // Add new state for storing domain suggestions from other elements
  const [existingDomainSuggestions, setExistingDomainSuggestions] = useState<{
    whitelist: string[];
    blacklist: string[];
  }>({
    whitelist: [],
    blacklist: [],
  });

  // Filter out CharityStack internal URLs
  const isCharityStackInternalURL = (domain: string): boolean => {
    const normalizedDomain = domain.toLowerCase().trim();
    if (/(?:^https?:\/\/)?(?:www\.)?charitystack\.com\//i.test(normalizedDomain)) {
      return true;
    }
    return false;
  };

  // Add form reference
  const formRef = useRef<HTMLFormElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  // Update formData helper
  const updateFormData = (field: keyof AddToCartFormData, value: any): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle field focus to clear validation errors
  const handleFieldFocus = (fieldName: string) => {
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      // Remove the exact field name
      newInvalidFields.delete(fieldName);
      // Also try to remove non-prefixed version for backward compatibility
      const nonPrefixedName = fieldName.includes('-')
        ? fieldName.split('-').slice(1).join('-')
        : fieldName;
      newInvalidFields.delete(nonPrefixedName);
      return newInvalidFields;
    });
  };

  // Function to check if a field is invalid
  const isFieldInvalid = (fieldName: string) => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      // Check for exact match first
      if (invalidFields.has(fieldName)) {
        return true;
      }
      // Check for prefixed versions
      for (const tab of tabs) {
        if (invalidFields.has(`${tab.step}-${fieldName}`)) {
          return true;
        }
      }
      return false;
    }
    return false;
  };

  // Fetch domain suggestions from existing elements
  useEffect(() => {
    const fetchExistingDomains = async (): Promise<void> => {
      if (!props.open) return; // Don't fetch if drawer is closed

      try {
        const response = await fetch(
          `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements?type=whitelist_and_blacklist`,
          {
            method: 'GET',
            headers: {
              Authorization: await getAuthToken(),
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) throw new Error('Failed to fetch domain suggestions');

        const data = await response.json();

        // The API returns { whitelist: [], blacklist: [] } directly, not { elements: [] }
        const allWhitelist = new Set<string>();
        const allBlacklist = new Set<string>();

        // Add domains from API response
        (data.whitelist || []).forEach((domain: string) => {
          if (!isCharityStackInternalURL(domain)) {
            allWhitelist.add(domain);
          }
        });

        (data.blacklist || []).forEach((domain: string) => {
          if (!isCharityStackInternalURL(domain)) {
            allBlacklist.add(domain);
          }
        });

        const filteredWhitelist = Array.from(allWhitelist);
        const filteredBlacklist = Array.from(allBlacklist);

        setExistingDomainSuggestions({
          whitelist: filteredWhitelist,
          blacklist: filteredBlacklist,
        });
      } catch (error) {
        console.error('❌ Error fetching domain suggestions:', error);
      }
    };

    fetchExistingDomains();
  }, [props.open]);

  // Handle form submission
  const handleSubmit = async (): Promise<void> => {
    const newInvalidFields = new Set<string>();

    if (!formData.elementName.trim()) {
      newInvalidFields.add('configuration-elementName');
    }
    if (formData.connectedForms.length === 0) {
      newInvalidFields.add('configuration-connectedForms');
    }
    if (!formData.allowedDomains || formData.allowedDomains.length === 0) {
      newInvalidFields.add('configuration-allowedDomains');
    }
    if (!formData.color || !formData.color.trim()) {
      newInvalidFields.add('appearance-color');
    }
    if (formData.sticky === null) {
      newInvalidFields.add('appearance-sticky');
    }

    if (newInvalidFields.size > 0) {
      setInvalidFields(newInvalidFields);
      focusFirstInvalidElement(rightPanelRef.current);
      toast.error('Please fix validation errors');
      return;
    }

    setIsSaving(true);

    try {
      const payload: any = {
        sticky: formData.sticky,
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        CSMerchantID: props.merchant?.organizationId || '',
        whitelist: formData.allowedDomains || [],
        blacklist: formData.disallowedDomains || [],
        color: formData.color,
        elementName: formData.elementName,
        elementType: 'ADD_TO_CART',
        customShareLink: '',
        formNames: formData.connectedForms,
        formIDs: formData.connectedForms
          .map((formName) => availableForms.find((form) => form.formName === formName)?.formID)
          .filter((id) => id !== undefined),
        active: publicationStateForSave({ existingId: elementID, active, paymentsReady }),
        ...(!elementID && {
          timeCreated: new Date().toISOString(),
          createdBy: props.user?.email || 'unknown',
        }),
      };

      if (elementID) {
        payload.elementID = elementID;
        delete payload.active;
      }

      const method = elementID ? 'PATCH' : 'POST';
      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          toast.error(data.error);
        } catch {
          throw new Error('API request failed');
        }
        return;
      }

      const data = await response.json();

      toast.success(
        elementID
          ? 'Element updated successfully'
          : paymentsReady
            ? 'Element published successfully'
            : 'Draft saved successfully',
      );
      if (!elementID) {
        setActive(data.active ?? payload.active);
        setElementID(data.elementID);
      }
      if (props.onSuccess) props.onSuccess(data);
    } catch (error: any) {
      console.error('Submission error:', error);
      toast.error(`Failed to ${elementID ? 'update' : 'publish'} element`, {
        description: error?.message || 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Activate/Deactivate element
  const activateElement = async (): Promise<void> => {
    setIsActivating(true);
    const state = !active;
    if (state && !paymentsReady) {
      setIsActivating(false);
      toast.info('Enable payments before publishing this draft.');
      return;
    }
    const successAlertMessage = state ? 'Element Activated' : 'Element Deactivated';

    try {
      const payload = {
        elementID,
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        active: state,
      };

      const requestOptions = {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      };

      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements/toggle-active`;
      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
        return;
      } else {
        toast.success(successAlertMessage);
        setActive(state);
        if (props.onSuccess) props.onSuccess(data);
      }
    } catch (error) {
      console.error('Activation error:', error);
      toast.error('Failed to update element status');
    } finally {
      setIsActivating(false);
    }
  };

  // Fetch forms
  useEffect(() => {
    const fetchForms = async (): Promise<void> => {
      if (!props.open) return;

      try {
        const merchants = await getAuthGroups();

        const response = await portalClient.postFormAndFundData({
          authorization: await getAuthToken(),
          body: {
            queryStringParameters: {
              nonprofit: merchants[0],
            },
          },
        });

        const data = await response.json();
        setAvailableForms(data.formNames);
      } catch (error) {
        console.error('Error fetching forms:', error);
        setAvailableForms([]);
      }
    };

    fetchForms();
  }, [props.open]);

  // Populate form data for edit mode
  useEffect(() => {
    if (props.elementData && props.open) {
      const data = props.elementData;
      const filteredWhitelist = (data.whitelist || []).filter(
        (domain) => !isCharityStackInternalURL(domain),
      );

      setElementID(data.elementID);
      setFormData({
        elementName: data.elementName,
        connectedForms: data.formNames || [],
        allowedDomains: filteredWhitelist,
        disallowedDomains: data.blacklist || [],
        customShareLink: '',
        color: data.color,
        sticky: data.sticky,
      });
      setActive(data.active);
      setCurrentStep('configuration');
    }
  }, [props.elementData, props.open]);

  // Pre-fill the element color from the org's brand color when the drawer opens.
  // Only applies when (a) the drawer is open, (b) the org has a brand color set, and
  // (c) the color field is still untouched (empty) — so a color the user has picked
  // or one loaded from an existing element in edit mode is never overwritten.
  useEffect(() => {
    if (!props.open) return;
    const orgBrandColor = props.merchant?.brandColor;
    if (!orgBrandColor) return;
    setFormData((prev) => {
      if (prev.color) return prev; // user picked one, or edit-mode loaded one
      return { ...prev, color: orgBrandColor };
    });
  }, [props.open, props.merchant?.brandColor]);

  const clearStates = (): void => {
    setFormData(initialFormData);
    setElementID(null);
    setAvailableForms([]);
    setInvalidFields(new Set());
    setCurrentStep('configuration');
    setSketchPickerColor({ r: 0, g: 0, b: 0, a: 1 });
    setDisplayColorPicker(false);
    setActive(true);
    setIsSaving(false);
    setIsActivating(false);

    if (formRef.current) formRef.current.reset();
  };

  const handleSubmitLatest = useLatestCallback(handleSubmit);
  const activateElementLatest = useLatestCallback(activateElement);

  // Don't render if required props are missing
  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions: WorkspaceHeaderAction[] = [];

    if (elementID) {
      actions.push(
        {
          key: 'save',
          label: isSaving ? 'Saving...' : 'Save',
          variant: 'primary' as const,
          disabled: isSaving || isActivating,
          onClick: () => handleSubmitLatest(),
        },
        {
          key: 'active',
          label: !active
            ? isActivating
              ? 'Activating...'
              : 'Activate'
            : isActivating
              ? 'Deactivating...'
              : 'Deactivate',
          disabled: isActivating || isSaving || (!active && !paymentsReady),
          variant: !active ? 'primary' : 'danger',
          onClick: () => activateElementLatest(),
        },
      );
    } else {
      actions.push({
        key: 'publish',
        label: isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft',
        disabled: isSaving || isActivating,
        variant: 'primary' as const,
        onClick: () => handleSubmitLatest(),
      });
    }

    props.setWorkspaceActions(actions);
    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    isSaving,
    isActivating,
    elementID,
    active,
    paymentsReady,
    handleSubmitLatest,
    activateElementLatest,
  ]);

  // Don't render if required props are missing
  if (!props.merchant) {
    return null;
  }

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        props.setOpen(open);
        if (!open) clearStates();
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        className={
          props.presentation === 'workspace'
            ? 'min-h-full overflow-visible rounded-none bg-white p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
        onEscapeKeyDown={() => clearStates()}
        onPointerDownOutside={() => clearStates()}
      >
        {/* Main Container - centered */}
        <div
          className={`mx-auto w-full max-w-[1600px] ${
            props.presentation === 'workspace' ? 'px-3 py-2 md:py-3' : 'h-full pointer-events-none'
          }`}
        >
          {/* Header with Action Buttons */}
          {props.presentation !== 'workspace' && (
            <div className="pointer-events-auto mb-4 flex items-center justify-between rounded-lg bg-white p-0">
              <div className="flex items-start gap-x-[6px]">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-6 w-6"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.5 6v.75H5.513c-.96 0-1.764.724-1.865 1.679l-1.263 12A1.875 1.875 0 004.25 22.5h15.5a1.875 1.875 0 001.865-2.071l-1.263-12a1.875 1.875 0 00-1.865-1.679H16.5V6a4.5 4.5 0 10-9 0zM12 3a3 3 0 00-3 3v.75h6V6a3 3 0 00-3-3zm-3 8.25a3 3 0 106 0v-.75a.75.75 0 011.5 0v.75a4.5 4.5 0 11-9 0v-.75a.75.75 0 011.5 0v.75z"
                      clipRule="evenodd"
                      fill="#6B6458"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-base text-ink-muted">
                    {elementID ? 'Edit Add to Cart' : 'Create Add to Cart'}
                  </p>
                  <p className="text-sm text-ink-muted">
                    Add a sticky cart button that follows users as they browse
                  </p>
                </div>
              </div>

              <div className="flex flex-row items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    props.setOpen(false);
                    clearStates();
                  }}
                >
                  Close
                </Button>

                {/* Save Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={isSaving || isActivating}
                    variant="primary"
                    size="workspace"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                )}

                {/* Activate/Deactivate Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => activateElement()}
                    disabled={isActivating || isSaving || (!active && !paymentsReady)}
                    variant={!active ? 'primary' : 'danger'}
                    size="workspace"
                  >
                    {!active
                      ? isActivating
                        ? 'Activating...'
                        : 'Activate'
                      : isActivating
                        ? 'Deactivating...'
                        : 'Deactivate'}
                  </Button>
                )}

                {/* Publish Button - Only when creating */}
                {!elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    variant="primary"
                    size="workspace"
                    disabled={isSaving || isActivating}
                  >
                    {isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {props.presentation !== 'workspace' && (
            <div className="pb-3">
              <Separator className="bg-paper-inset" />
            </div>
          )}

          {/* Main Content - Split Layout */}
          <div
            className={`pointer-events-auto flex w-full flex-col-reverse gap-6 lg:flex-row ${
              props.presentation === 'workspace'
                ? 'overflow-visible'
                : 'h-[calc(100%-80px)] overflow-y-auto'
            }`}
          >
            {/* Left Side - Preview Section */}
            <div className="preview-zoom-responsive flex min-h-[700px] flex-col gap-4 pr-2 lg:w-1/2">
              <div className="flex w-full flex-col items-stretch rounded-md border border-edge bg-paper-muted px-4 py-4">
                <AddToCartRender
                  elementType="add-to-cart"
                  key={formData.color}
                  colors={{ text: '#FFFFFF', background: formData.color || '#9A9285' }}
                />
              </div>
            </div>

            {/* Right Side - Form Configuration */}
            <div
              ref={rightPanelRef}
              className={`compact-editor-controls flex flex-col lg:w-1/2 lg:pl-4 ${
                useCreateWorkspaceCards
                  ? 'min-h-0 overflow-visible pb-0'
                  : 'min-h-[500px] overflow-hidden pb-14 lg:sticky lg:top-0 lg:h-[calc(100vh-160px)] lg:pb-0'
              }`}
            >
              {/* Form Tabs */}
              {!useCreateWorkspaceCards && (
                <div className="border-edge bg-white">
                  <nav
                    className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                    aria-label="Tabs"
                  >
                    {tabs.map((tab) => {
                      // Check if this tab has errors by looking for fields with the tab's step prefix
                      const tabHasErrors = Array.from(invalidFields).some((field) =>
                        field.startsWith(tab.step + '-'),
                      );
                      return (
                        <button
                          type="button"
                          key={tab.name}
                          onClick={() => setCurrentStep(tab.step)}
                          className={classNames(
                            currentStep === tab.step
                              ? tabHasErrors
                                ? 'border-red-500 text-red-600'
                                : 'border-ink-secondary text-ink-secondary'
                              : tabHasErrors
                                ? 'border-transparent text-red-500 hover:text-red-600'
                                : 'border-transparent text-ink-muted hover:text-ink-secondary',
                            'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                          )}
                        >
                          {tab.name}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              )}

              {/* Form Content */}
              <div
                className={`flex-1 ${
                  useCreateWorkspaceCards ? 'space-y-4 overflow-visible' : 'overflow-y-auto py-4'
                }`}
              >
                {/* Configuration Section */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'configuration'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-2'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Configuration"
                    description="Choose the forms, domains, and behavior for this cart element."
                    icon={elementEditorSectionIcons.configuration}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'configuration')}
                  >
                    <AddToCartConfiguration
                      formData={formData}
                      updateFormData={updateFormData}
                      availableForms={availableForms}
                      existingDomainSuggestions={existingDomainSuggestions}
                      isCharityStackInternalURL={isCharityStackInternalURL}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      invalidFields={invalidFields}
                      setInvalidFields={setInvalidFields}
                    />
                  </ElementEditorSection>
                </div>

                {/* Appearance Section */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'appearance'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-4'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Appearance"
                    description="Set the brand color and sticky presentation."
                    icon={elementEditorSectionIcons.appearance}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'appearance')}
                  >
                    <AppearanceTab
                      color={formData.color}
                      setColor={(value) => updateFormData('color', value)}
                      sketchPickerColor={sketchPickerColor}
                      setSketchPickerColor={setSketchPickerColor}
                      displayColorPicker={displayColorPicker}
                      setDisplayColorPicker={setDisplayColorPicker}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      invalidFields={invalidFields}
                      setInvalidFields={setInvalidFields}
                      elementType="add-to-cart"
                      sticky={formData.sticky}
                      setSticky={(value) => updateFormData('sticky', value)}
                    />
                  </ElementEditorSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
